// @ts-strict-ignore
import { SelectionModel } from '@project/components/article/model/selection.model';
import { ObjExporter } from '@3d-scene/exporters/obj-exporter';
import { GlobalContainer } from '@3d-scene/model3d/global-container';
import { ObjInfo, ObjTextureInfo } from '@article/model/obj-info';
import { Object3D, Vector3 } from 'three';
import { OBJRepresentationDataVO, OBJTextureDataVO, RepresentationVO } from '@shared/vo/representation.vo';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ConnectionAreaVO, MetaElementUsageIdentityVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { ProjectModel } from '@project/model/project.model';
import { StringUtils } from '@shared/util/string-utils';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { AdminToolService } from '../services/admintool.service';
import { map } from 'rxjs/operators';
import { ArrayUtils } from '@shared/util/array-utils';
import { MetaElementUtils } from '@article/components/connections/connection-creation/meta-element.utils';
import { ConnectionCreator } from '@article/components/connections/connection-creation/connection-creator';
import { Injectable } from '@angular/core';
import { ProjectUserModel } from '@project/model/project-user.model';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { ConfiguratorParameters } from '@3d-scene/configurator-parameters/configurator-parameters';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ArticleExportManager {
  constructor(
    private projectUserModel: ProjectUserModel,
    private globalContainer: GlobalContainer,
    private selectionModel: SelectionModel,
    private selectionModelService: SelectionModelService,
    private adminToolService: AdminToolService,
    private projectModel: ProjectModel,
    private connectionCreator: ConnectionCreator,
    private articleContainerUtils: ArticleContainerUtils,
    private connectionInfoService: ConnectionInfoService,
  ) {}

  public saveArticle(article: ArticleVO): Observable<unknown> {
    const selectedObjects: any[] = this.selectionModel.selectedObjects;
    this.selectionModelService.removeAllSelections();
    const exporter: ObjExporter = new ObjExporter(this.projectUserModel);
    const globalContainerData: ObjInfo = exporter.export(
      this.globalContainer,
      true,
      true,
      false,
      false,
      true,
      ConfiguratorParameters.inverseTextureCoordinates,
      false,
      (object3D: Object3D): Boolean => {
        return (
          this.articleContainerUtils.isObjectRelevant(object3D) &&
          object3D.name !== Connection.DEFAULT_CONNECTION_OUTLINE_NAME
        );
      }
    );

    const objRepresentationData: OBJRepresentationDataVO = this.convertObjInfoToRepresentationData(globalContainerData);
    this.updateOrderNumberAndCorridorOfInternalConnections(article);
    this.updateCorridorInformationOfGivingConnections(article);
    if (
      article.representations.findIndex((rep) => rep.type === RepresentationVO.TYPE_VIEW3D) < 0 &&
      StringUtils.isNotEmpty(objRepresentationData.modelDataString)
    ) {
      const view3dRepresentationForSavePurposes = new RepresentationVO();
      view3dRepresentationForSavePurposes.data = objRepresentationData;
      view3dRepresentationForSavePurposes.type = RepresentationVO.TYPE_VIEW3D;
      article.representations.push(view3dRepresentationForSavePurposes);
    }
    return this.adminToolService.saveArticle(article, objRepresentationData).pipe(
      map(() => {
        if (ArrayUtils.isNotEmpty(selectedObjects)) {
          this.selectionModelService.selectObject(selectedObjects, true, false);
        }
      })
    );
  }

  public convertObjInfoToRepresentationData(objInfo: ObjInfo): OBJRepresentationDataVO {
    const objRepresentationData: OBJRepresentationDataVO = new OBJRepresentationDataVO();
    objRepresentationData.textures = [];
    objRepresentationData.modelDataString = objInfo.objContent;
    objRepresentationData.materialLibraryDataString = objInfo.mtlContent;
    objInfo.textures.forEach((textureInfo: ObjTextureInfo) => {
      const texture: OBJTextureDataVO = new OBJTextureDataVO();
      texture.name = textureInfo.name;
      texture.content = textureInfo.content;
      texture.url = textureInfo.url;
      texture.articleParameters = textureInfo.articleParameters;
      objRepresentationData.textures.push(texture);
    });
    return objRepresentationData;
  }

  private updateOrderNumberAndCorridorOfInternalConnections(article: ArticleVO): void {
    if (!article) {
      return;
    }
    const mainMetaElements: MetaElementVO[] = article.metaElements.filter(
      (meta: MetaElementVO) => !meta.isInternal && MetaElementUtils.isMultipurposeArea(meta)
    );
    for (const mainMetaElement of mainMetaElements) {
      const linkedMetaElements: MetaElementVO[] = this.connectionCreator.getMultipurposeAreasByConnectionName(mainMetaElement.name, article);
      linkedMetaElements.forEach((linkedMetaElement: MetaElementVO) => {
        if (linkedMetaElement.isInternal) {
          linkedMetaElement.orderNumber = mainMetaElement.orderNumber;
          linkedMetaElement.corridor = mainMetaElement.corridor;
        }
      });
    }
  }

  private updateCorridorInformationOfGivingConnections(article: ArticleVO): void {
    if (article == null) {
      return;
    }
    const givingConnections: ConnectionAreaVO[] = [];
    article.metaElements.forEach((metaElement: MetaElementVO) => {
      if (metaElement instanceof ConnectionAreaVO) {
        const connectionArea: ConnectionAreaVO = metaElement as ConnectionAreaVO;
        if (connectionArea.connectionSide === ConnectionAreaVO.SIDE_GIVING) {
          givingConnections.push(connectionArea);
        }
      }
    });
    const articleUsage = ProjectUtils.tryGetLastArticleUsage(this.projectModel.currentProject);
    if(!articleUsage) {
      return;
    }
    const givingConnectionContainers: Connection[] = [];
    givingConnections.forEach((givingConnection: ConnectionAreaVO) => {
      const connectionContainer: Connection = this.connectionInfoService.findConnectionByIdentity(
        MetaElementUsageIdentityVO.create(articleUsage.id, givingConnection.name)
      );
      if (connectionContainer != null) {
        givingConnectionContainers.push(connectionContainer);
      }
    });
    let compareTargetIndex: number = -1;
    let maxDistance: number = NaN;
    for (let i: number = 0; i < givingConnectionContainers.length; i++) {
      const sourceConnection: Connection = givingConnectionContainers[i];
      for (let j: number = i + 1; j < givingConnectionContainers.length; j++) {
        const targetConnection: Connection = givingConnectionContainers[j];
        const sourceConnectionPosition: Vector3 = sourceConnection.mesh.position.clone().add(sourceConnection.mesh.geometry.boundingSphere.center);
        const targetConnectionPosition: Vector3 = targetConnection.mesh.position.clone().add(targetConnection.mesh.geometry.boundingSphere.center);
        const distance: number = sourceConnectionPosition.distanceTo(targetConnectionPosition);
        if (isNaN(maxDistance) || distance > maxDistance) {
          compareTargetIndex = i;
          maxDistance = distance;
        }
      }
    }
    if (compareTargetIndex !== -1) {
      givingConnectionContainers.sort(this.getCompareConnectionsByLocationFunction(givingConnectionContainers[compareTargetIndex]));
    }
    givingConnections.forEach((givingConnection: ConnectionAreaVO) => {
      const order: number = this.getIndexByConnectionName(givingConnectionContainers, givingConnection.name);
      givingConnection.corridor = order;
    });
  }

  private getIndexByConnectionName(givingConnectionContainers: Connection[], connectionName: String): number {
    if (givingConnectionContainers == null) {
      return -1;
    }
    for (let i: number = 0; i < givingConnectionContainers.length; i++) {
      const givingConnectionContainer: Connection = givingConnectionContainers[i];
      if (givingConnectionContainer.connectionName === connectionName) {
        return i;
      }
    }
    return -1;
  }

  private getCompareConnectionsByLocationFunction(compareTarget: Connection): (a: Connection, b: Connection) => number {
    return function compareConnectionsByLocation(a: Connection, b: Connection): number {
      const aConnection: Connection = a;
      const bConnection: Connection = b;
      const compareTargetPosition: Vector3 = compareTarget.mesh.position.clone().add(compareTarget.mesh.geometry.boundingSphere.center);
      const aConnectionPosition: Vector3 = aConnection.mesh.position.clone().add(aConnection.mesh.geometry.boundingSphere.center);
      const bConnectionPosition: Vector3 = bConnection.mesh.position.clone().add(bConnection.mesh.geometry.boundingSphere.center);
      const aDistance: number = compareTargetPosition.distanceTo(aConnectionPosition);
      const bDistance: number = compareTargetPosition.distanceTo(bConnectionPosition);
      if (aDistance < bDistance) {
        return -1;
      } else if (aDistance > bDistance) {
        return 1;
      }
    };
  }
}
